// frontend/src/pages/manufacturing/v2/ShopFloorTerminalV2.jsx
//
// V2 shop-floor terminal — a 1-to-1 visual + functional port of v1's
// `pages/shopfloor/ShopFloorTerminal.js`, but every backend call goes
// through the `/api/v2/...` namespace and the v2 ledger / step actions
// (start / complete / scrap / cancel / adjust).
//
// Structural parity with v1:
//   - Same header card layout (Build icon + title + Refresh)
//   - Same Active / Completed tabs
//   - Same column order: Work Order & Urgency | What to Make | Need Now |
//     Total Demand | Material Status | Customer | Due Date | Progress | Actions
//   - Same expandable row with work-details / DemandTimelineDisplay /
//     MaterialRequirementsTable in a 2-column grid
//   - Same date-group banners (overdue / today / tomorrow / this week / future / no date)
//     with identical bgcolor mapping to v1
//   - Same Group-by-Date toggle + DatePicker
//   - Same sort options (Item A-Z / Z-A / urgency / due-date / WO#)
//   - Same EnhancedProductionDialog for complete (qty good + scrap + notes)
//   - Same MaterialShortageDialog when materials are missing
//   - Same admin Edit / Delete dialogs — mapped to v2 `adjust` / `cancel`
//
// Differences from v1 (intentional — see feature inventory in the task report):
//   - No ShopFloorLogin / Logout: v2 lives in the main app shell.
//   - DemandTimelineDisplay shows a single-line summary (v2 has one WO per
//     customer-order line — no cross-order aggregation to display).
//   - EditCompletedWorkOrderDialog is replaced by a tooltip explaining the v2
//     ledger is append-only — admin Edit maps to `adjust` for legitimate
//     corrections.
//   - Auto-7pm logout uses the shared useTerminalCurfew hook: it fires only
//     when 19:00 passes while this page is visible, so re-logging-in after
//     7pm never bounces the user, and a background tab can't kill the
//     session for the rest of the app.
//
// View-mode toggle (Individual / Consolidated) is preserved next to Refresh.
//
// ---------------------------------------------------------------------------
// PARITY AUDIT (v1 ⇄ v2)
// ---------------------------------------------------------------------------
// 1. Row inclusion (server-side):
//    v1: status IN (planned, released, in_progress); cancelled excluded.
//    v2: same — see backend/api/wo_v2/views.py::ShopFloorV2View. Also hides
//        rows whose linked CO line is fully shipped (qty_ordered<=shipped),
//        mirroring v1 commit 8c33252.
// 2. Sorting: 7 options match v1 — item_name asc/desc, urgency, due_date
//    asc/desc, wo_number asc/desc. Item-name tiebreaks on sequence (v1:
//    step_number). Urgency tiebreaks on due_date then wo_number (v1 relies
//    on JS sort stability — v2 makes it explicit). due_date sorts push
//    nulls to end (matches v1).
// 3. Urgency thresholds: OVERDUE (days<0), URGENT (days<=1), HIGH (days<=7),
//    NORMAL otherwise; no due_date => NORMAL. v1 has a secondary path that
//    reads demand_summary buckets — N/A in v2 because v2 has one WO per
//    line so demand_summary doesn't exist; the due_date path is the only
//    one needed.
// 4. Date-group banner colours / labels: match v1 exactly
//    (error.main for overdue/today, warning.main for tomorrow,
//    info.main for ≤7d, info.light for ≤14d, default beyond).
// 5. Chips: urgency chip, multi-step "Step n/N", dependency-blocker alert,
//    material status chip — all match v1.
// 6. Start button enable rules: disabled when loading, fully complete, or
//    materials are status='none'. Multi-step dependency renders as BLOCKED
//    with override-via-confirm. MaterialAwareStartButtonV2 also caps
//    max_producible by qty_available_from_previous_step (v1 parity).
// 7. Completion safety: client-side hard block prevents qty_good >
//    qty_available_from_previous_step on multi-step rows (v1 parity).
//    Overproduction triggers a confirm dialog with the extra-units count.
// 8. Search (Completed tab): wo_number, output_item_name, output_item_code.
// 9. Operation switcher: shows every operation (v1 doesn't gate on has-open
//    -WOs or is_active either).
// 10. Auto-refresh: 30s, paused during any dialog, only on per-op pages
//     (not "all"). Matches v1.
// 11. Completed tab: date-picker defaults to today; backend filters by
//     completed_at date; client-side search applies on top.
// 12. Active-tab "X URGENT" pill: v1 counts WOs with priority<=2; v2
//     counts rows whose computed urgency is OVERDUE. INTENTIONAL: v2 doesn't
//     surface the legacy priority field, and the v2 model encodes urgency
//     via due_date — keeping the date-based count is the v2-native answer.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, LinearProgress, Stack, Tooltip, Tabs, Tab, Collapse,
  CircularProgress, InputAdornment, ToggleButtonGroup, ToggleButton, Paper,
  Divider, MenuItem,
} from '@mui/material';
import {
  PlayArrow, CheckCircle, Warning, Refresh, Assignment, Build, Inventory,
  ExpandMore, ExpandLess, Info, Timeline, Block,
  Error as ErrorIcon, History, Today, Search, CalendarMonth, Link as LinkIcon,
  Edit, Delete, Lock, ReportProblem, ExitToApp, ShoppingCart, Download, Undo,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { toast } from 'react-toastify';

import { v2, manufacturingAPI } from '../../../services/api';
import usePageTitle from '../../../hooks/usePageTitle';
import { splitHighlightRules } from './utils/highlightText';
import HighlightBarsV2 from './components/HighlightBarsV2';
import { useAuth } from '../../../contexts/AuthContext';
import GenerationModeBadge from './components/GenerationModeBadge';
import useTerminalCurfew from '../../../hooks/useTerminalCurfew';
import RootItemsDisplayV2 from './components/RootItemsDisplayV2';
import LinkedOrdersDisplayV2 from './components/LinkedOrdersDisplayV2';
import DemandTimelineCellV2 from './components/DemandTimelineCellV2';
import MaterialStatusChipV2 from './components/MaterialStatusChipV2';
import MaterialAwareStartButtonV2 from './components/MaterialAwareStartButtonV2';
import MaterialRequirementsTableV2 from './components/MaterialRequirementsTableV2';
import MaterialShortageDialogV2 from './components/MaterialShortageDialogV2';
import GroupProductionDialogV2 from './components/GroupProductionDialogV2';
import OrderSummaryDialogV2 from './components/OrderSummaryDialogV2';
import AdminWOUndoCompletionDialogV2 from './components/AdminWOUndoCompletionDialogV2';
import ScrapComponentDialogV2 from './components/ScrapComponentDialogV2';
import ScrapStripV2 from './components/ScrapStripV2';
import { orderStatusColor, orderStatusLabel } from './utils/orderStatus';
import { groupForConsolidatedView } from './utils/groupForConsolidatedView';
import {
  StepRowV2, GroupRowV2, StepIndicator, getUrgencyLevel,
} from './components/ShopFloorRowsV2';

const VIEW_MODE_KEY = 'wo_v2_view_mode';

// Chunked rendering: the first RENDER_CHUNK top-level rows mount
// immediately; the rest stream in batch-by-batch during browser idle time.
// This bounds the initial render commit so a large "All Operations" load
// (due-window filter off) can never block the main thread long enough to
// trigger the browser's "page isn't responding" dialog.
const RENDER_CHUNK = 80;

// On the all-operations page the idle-time streaming used to keep going
// until every row (up to the backend's 500-step cap) was in the DOM, and
// 400+ live MUI table rows make scrolling visibly janky. Cap automatic
// mounting there — the operator pulls further pages with the "Show more"
// button. Per-operation pages have small row counts and keep the
// mount-everything behaviour; a date drill-in (filterDate) still shows all.
const ALL_OPS_RENDER_CAP = 160;

// State updater guard for polled data: return the PREVIOUS array when the
// fresh fetch is byte-identical, so unchanged polls keep referential
// identity (memoized rows skip re-render; downstream effects don't fire).
function keepIfUnchanged(prev, next) {
  try {
    return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
  } catch (_) {
    return next;
  }
}

const SORT_OPTIONS = [
  { value: 'due_date_asc', label: 'Due Date (Earliest First)' },
  { value: 'due_date_desc', label: 'Due Date (Latest First)' },
  { value: 'urgency', label: 'Urgency (Most Urgent First)' },
  { value: 'item_name_asc', label: 'Item Name (A-Z)' },
  { value: 'item_name_desc', label: 'Item Name (Z-A)' },
  { value: 'wo_number_asc', label: 'Work Order # (Ascending)' },
  { value: 'wo_number_desc', label: 'Work Order # (Descending)' },
];

// The chosen sort survives refreshes, remounts and operation switches —
// same persistence pattern as view mode / group-by-date above. Without
// this, anything that remounted the terminal silently snapped the list
// back to the hardcoded default moments after the user picked a sort.
const SORT_ORDER_KEY = 'wo_v2_sf_sort_order';

function readStoredSortOrder() {
  try {
    const v = window.localStorage.getItem(SORT_ORDER_KEY);
    if (v && SORT_OPTIONS.some((o) => o.value === v)) return v;
  } catch (_) { /* ignore */ }
  // Default: due soonest first — the floor works today's list top-down.
  return 'due_date_asc';
}
function writeStoredSortOrder(v) {
  try { window.localStorage.setItem(SORT_ORDER_KEY, v); } catch (_) { /* ignore */ }
}

// View mode is remembered PER OPERATION (…_OP-12, …_OP-15, …_all): an
// operator can keep OP-12 on individual and OP-15 on consolidated without
// re-toggling on every visit.
function viewModeKey(opCode) {
  return `${VIEW_MODE_KEY}_${opCode || 'all'}`;
}
function readStoredViewMode(opCode) {
  try {
    const v = window.localStorage.getItem(viewModeKey(opCode))
      // Legacy single global key — honoured until an op-specific choice is saved.
      || window.localStorage.getItem(VIEW_MODE_KEY);
    if (v === 'individual' || v === 'consolidated') return v;
  } catch (_) { /* ignore */ }
  // First visit (nothing stored): consolidated groups read best on the floor.
  return 'consolidated';
}
function writeStoredViewMode(opCode, v) {
  try { window.localStorage.setItem(viewModeKey(opCode), v); } catch (_) { /* ignore */ }
}

// Sorts that organise the list by date — the only ones the OVERDUE/TODAY/
// date banners make sense for. See `dateBannersApply` below.
const DATE_SECTION_SORTS = new Set(['due_date_asc', 'due_date_desc', 'urgency']);

// Group-by-date survives page refreshes and operation switches.
const GROUP_BY_DATE_KEY = 'wo_v2_sf_group_by_date';

function readStoredGroupByDate() {
  try {
    const v = window.localStorage.getItem(GROUP_BY_DATE_KEY);
    if (v === 'true' || v === 'false') return v === 'true';
  } catch (_) { /* ignore */ }
  // First visit (nothing stored): group by date so the day's work reads
  // top-to-bottom. An explicit user toggle always wins over this default.
  return true;
}
function writeStoredGroupByDate(v) {
  try { window.localStorage.setItem(GROUP_BY_DATE_KEY, String(Boolean(v))); } catch (_) { /* ignore */ }
}

// "Overdue + 2 wks" due-window filter survives refreshes / operation switches,
// mirroring the Group-by-Date persistence pattern above. When ON, loadData
// sends due_window_days=14 so only overdue / due-soon / undated rows load
// (demand totals on the returned rows still cover all dates).
//
// Default: ON for the all-operations page, OFF for per-operation pages —
// "all" is the one view that otherwise loads every open step in the factory
// in a single render (the cause of "page isn't responding" on slower
// browsers). An explicit user toggle is stored and wins on every page;
// readStoredDueWindow returns null when the user has never chosen.
const DUE_WINDOW_KEY = 'shopFloorV2DueWindow';
const DUE_WINDOW_DAYS = 14;

function readStoredDueWindow() {
  try {
    const v = window.localStorage.getItem(DUE_WINDOW_KEY);
    if (v === 'true' || v === 'false') return v === 'true';
  } catch (_) { /* ignore */ }
  return null;
}
function writeStoredDueWindow(v) {
  try { window.localStorage.setItem(DUE_WINDOW_KEY, String(Boolean(v))); } catch (_) { /* ignore */ }
}

// "Total Demand" column is optional — hidden by default to keep the table
// tight. Persisted like the other view toggles.
const SHOW_DEMAND_KEY = 'wo_v2_sf_show_demand';

function readStoredShowDemand() {
  try {
    return window.localStorage.getItem(SHOW_DEMAND_KEY) === 'true';
  } catch (_) { return false; }
}
function writeStoredShowDemand(v) {
  try { window.localStorage.setItem(SHOW_DEMAND_KEY, String(Boolean(v))); } catch (_) { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Pure helpers (mirrors v1's date / urgency logic exactly)
// ---------------------------------------------------------------------------

function naturalSort(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
}

// Within-day ordering for the due-date sorts: item name A–Z, then step
// sequence, then WO# so ties are stable.
function itemNameTiebreak(a, b) {
  const na = (a.output_item_name || '').toLowerCase();
  const nb = (b.output_item_name || '').toLowerCase();
  if (na !== nb) return na.localeCompare(nb);
  const seq = (a.sequence || 1) - (b.sequence || 1);
  if (seq !== 0) return seq;
  return naturalSort(a.wo_number, b.wo_number);
}

// getUrgencyLevel / getPriorityColor / progressPercentage / StepIndicator /
// DependencyBlockerAlert moved to components/ShopFloorRowsV2.jsx (shared with
// the memoized row components); getUrgencyLevel + StepIndicator re-imported
// above for the sort logic and the Completed tab.

function getDateGroupColor(dateStr) {
  if (!dateStr || dateStr === 'no-date') return 'text.disabled';
  const days = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'error.main';
  if (days === 0) return 'error.main';
  if (days === 1) return 'warning.main';
  if (days <= 7) return 'info.main';
  if (days <= 14) return 'info.light';
  return 'text.secondary';
}

function isToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d.toDateString() === new Date().toDateString();
}

// ---------------------------------------------------------------------------
// Enhanced Production (Complete) dialog — mirrors v1's EnhancedProductionDialog
// ---------------------------------------------------------------------------

function EnhancedProductionDialogV2({ open, onClose, selectedStep, onComplete }) {
  const [data, setData] = useState({ quantity_completed: '', quantity_scrapped: '0', notes: '' });
  const [showCalc, setShowCalc] = useState(false);
  // "Scrapped parts": parts of the unit ruined and remade on the spot (a
  // table top, not the table). Each is posted to the WO scrap endpoint after
  // the sign-off — the raw material comes out of stock, the WO's good-unit
  // target is untouched (services/scrap.py). `components` is the step's
  // material list for the picker.
  const [components, setComponents] = useState([]);
  const [scrappedParts, setScrappedParts] = useState([]);
  // Material-shortage gate: when the operator opens the dialog we ask the
  // backend how many units can actually be produced given current stock. If
  // less than the remaining qty, we default `quantity_completed` to the
  // achievable number and show a banner explaining why.
  const [maxProducible, setMaxProducible] = useState(null);
  const [shortageInfo, setShortageInfo] = useState(null); // { shortages: [...] } or null
  const [checkingAvail, setCheckingAvail] = useState(false);

  useEffect(() => {
    if (!open || !selectedStep) return undefined;
    let cancelled = false;
    const total = parseFloat(selectedStep.quantity_to_produce) || 0;
    const done = parseFloat(selectedStep.quantity_completed) || 0;
    const remaining = Math.max(0, total - done);

    // Seed an immediate default to the remaining qty so the field isn't blank
    // while we wait on the network call.
    setData({
      quantity_completed: String(remaining || total || ''),
      quantity_scrapped: '0',
      notes: '',
    });
    setShowCalc(false);
    setMaxProducible(null);
    setShortageInfo(null);
    setCheckingAvail(true);
    setScrappedParts([]);
    setComponents([]);
    (async () => {
      try {
        const res = await v2.workOrders.materialRequirements(
          selectedStep.work_order_id, { step: selectedStep.step_id },
        );
        if (cancelled) return;
        setComponents((res.data?.requirements || []).map((r) => ({
          item_id: r.item_id,
          item_code: r.item_code,
          item_name: r.item_name,
          per_unit: Math.round((parseFloat(r.required) || 0) * 1000) / 1000,
        })));
      } catch (err) {
        if (!cancelled) console.error('material-requirements prefetch failed', err);
      }
    })();

    (async () => {
      try {
        const res = await v2.workOrders.materialAvailability(
          selectedStep.work_order_id,
          { quantity: remaining || 1, step: selectedStep.step_id },
        );
        if (cancelled) return;
        const max = parseFloat(res.data?.max_producible ?? '0');
        setMaxProducible(Number.isNaN(max) ? null : max);
        // If anything is short, default the qty field to whatever IS achievable
        // (capped at remaining), and surface the shortage list.
        const shortages = res.data?.shortages || [];
        if (shortages.length > 0) {
          setShortageInfo({ shortages });
          const cap = Math.max(0, Math.min(remaining, Number.isNaN(max) ? 0 : max));
          setData((p) => ({ ...p, quantity_completed: String(cap) }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('material-availability prefetch failed', err);
          // Leave the default as remaining — handleComplete will still
          // perform its own check before posting.
        }
      } finally {
        if (!cancelled) setCheckingAvail(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, selectedStep]);

  if (!selectedStep) return null;

  const remaining = Math.max(
    0,
    (parseFloat(selectedStep.quantity_to_produce) || 0)
      - (parseFloat(selectedStep.quantity_completed) || 0),
  );
  const isFinalStep =
    !selectedStep.total_steps
    || selectedStep.sequence === selectedStep.total_steps;

  const addPart = () => {
    const first = components.find((c) => !scrappedParts.some((p) => p.item_id === c.item_id))
      || components[0];
    if (!first) return;
    setScrappedParts((prev) => [
      ...prev,
      { key: Date.now() + prev.length, item_id: first.item_id, quantity: String(first.per_unit || 1), reason: '' },
    ]);
  };
  const updatePart = (key, patch) => setScrappedParts((prev) => prev.map((p) => {
    if (p.key !== key) return p;
    const next = { ...p, ...patch };
    if (patch.item_id != null && patch.item_id !== p.item_id) {
      const comp = components.find((c) => c.item_id === patch.item_id);
      if (comp) next.quantity = String(comp.per_unit || 1);
    }
    return next;
  }));
  const removePart = (key) => setScrappedParts((prev) => prev.filter((p) => p.key !== key));
  const validParts = scrappedParts.filter((p) => p.item_id && (parseFloat(p.quantity) || 0) > 0);
  const unitsEntered = (parseFloat(data.quantity_completed) || 0) + (parseFloat(data.quantity_scrapped) || 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayArrow />
          Record Production: {selectedStep.wo_number}
          {selectedStep.total_steps > 1 && (
            <Chip
              label={`Step ${selectedStep.sequence}/${selectedStep.total_steps}`}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {selectedStep.total_steps > 1 && (
            <Alert severity="info" icon={<LinkIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Multi-Step Routing:</strong> This is Step {selectedStep.sequence} of{' '}
                {selectedStep.total_steps}.
                {selectedStep.sequence < selectedStep.total_steps && (
                  <> After completing this step, units will be released for Step {selectedStep.sequence + 1}.</>
                )}
                {selectedStep.sequence === selectedStep.total_steps && (
                  <> This is the final step — completing it will produce the finished item.</>
                )}
              </Typography>
            </Alert>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                  Making: {selectedStep.output_item_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedStep.output_item_code}
                </Typography>
                <Typography>
                  Operation: {selectedStep.operation_code}{' '}
                  {selectedStep.operation_name ? `- ${selectedStep.operation_name}` : ''}
                </Typography>
                <Typography>
                  Remaining: <strong>{remaining}</strong> units
                </Typography>
                {selectedStep.root_output_item_id
                  && selectedStep.root_output_item_id !== selectedStep.output_item_id && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>For:</strong>{' '}
                        {selectedStep.root_output_item_name || selectedStep.root_output_item_code}
                      </Typography>
                    </Box>
                )}
              </Alert>
              {checkingAvail && (
                <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={16} />}>
                  <Typography variant="caption">Checking material availability…</Typography>
                </Alert>
              )}
              {shortageInfo && !checkingAvail && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="material-cap-alert">
                  <Typography variant="body2" fontWeight="bold">
                    Material shortage — can only make {maxProducible ?? 0} of {remaining} requested
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                    Default quantity set to {maxProducible ?? 0}. Short on:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {shortageInfo.shortages.slice(0, 6).map((s) => (
                      <li key={s.item_id}>
                        <Typography variant="caption">
                          {s.item_code} — short {parseFloat(s.shortage)} ({parseFloat(s.available)} on hand, need {parseFloat(s.required)})
                        </Typography>
                      </li>
                    ))}
                    {shortageInfo.shortages.length > 6 && (
                      <li>
                        <Typography variant="caption">
                          + {shortageInfo.shortages.length - 6} more…
                        </Typography>
                      </li>
                    )}
                  </Box>
                </Alert>
              )}
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Quantity Completed *"
                  type="number"
                  value={data.quantity_completed}
                  onChange={(e) => setData((p) => ({ ...p, quantity_completed: e.target.value }))}
                  inputProps={{ min: 0, step: 0.01, 'data-testid': 'complete-qty-good' }}
                  helperText={
                    shortageInfo
                      ? `Capped at ${maxProducible ?? 0} by material shortage. Enter less to part-complete; more requires resolving shortages.`
                      : `Remaining ${remaining}. Less = partial completion (row stays open); more = overproduction (will prompt to confirm).`
                  }
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Whole units scrapped"
                  type="number"
                  value={data.quantity_scrapped}
                  onChange={(e) => setData((p) => ({ ...p, quantity_scrapped: e.target.value }))}
                  inputProps={{ min: 0, step: 0.01, 'data-testid': 'complete-qty-scrap' }}
                  helperText={`Whole ${selectedStep.output_item_name || 'units'} written off, on top of the completed quantity (optional). They don't count towards the remaining — replacements still need making. For a ruined part you are remaking, use "Scrapped parts" below instead.`}
                  variant="outlined"
                />

                {/* Scrapped parts — a component of the unit (a table top),
                    remade on the spot. Posted via the WO scrap endpoint
                    after sign-off; the unit itself is NOT scrapped. */}
                <Box data-testid="scrapped-parts">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">
                      Scrapped parts
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ruined and made again — not the whole {selectedStep.output_item_name || 'unit'}
                      </Typography>
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={addPart}
                      disabled={components.length === 0}
                      data-testid="add-scrapped-part"
                    >
                      + Add a part
                    </Button>
                  </Box>
                  {scrappedParts.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      {components.length === 0
                        ? 'This step has no components to scrap.'
                        : 'None. Add one if a part was damaged and remade — its raw material comes out of stock.'}
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {scrappedParts.map((part) => (
                        <Box
                          key={part.key}
                          sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}
                          data-testid={`scrapped-part-${part.key}`}
                        >
                          <TextField
                            select
                            size="small"
                            label="Part"
                            value={String(part.item_id)}
                            onChange={(e) => updatePart(part.key, { item_id: Number(e.target.value) })}
                            sx={{ flex: 2, minWidth: 200 }}
                            inputProps={{ 'data-testid': `scrapped-part-item-${part.key}` }}
                          >
                            {components.map((c) => (
                              <MenuItem key={c.item_id} value={String(c.item_id)}>
                                {c.item_code} — {c.item_name} ({c.per_unit} per unit)
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            size="small"
                            label="Qty"
                            type="number"
                            value={part.quantity}
                            onChange={(e) => updatePart(part.key, { quantity: e.target.value })}
                            inputProps={{ min: 0, step: 'any', 'data-testid': `scrapped-part-qty-${part.key}` }}
                            sx={{ width: 100 }}
                          />
                          <TextField
                            size="small"
                            label="Reason"
                            value={part.reason}
                            onChange={(e) => updatePart(part.key, { reason: e.target.value })}
                            inputProps={{ 'data-testid': `scrapped-part-reason-${part.key}` }}
                            sx={{ flex: 2, minWidth: 160 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removePart(part.key)}
                            aria-label="Remove part"
                            data-testid={`remove-scrapped-part-${part.key}`}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
                <TextField
                  fullWidth
                  label="Notes (optional)"
                  multiline
                  rows={3}
                  value={data.notes}
                  onChange={(e) => setData((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Any issues, observations, or special notes..."
                  variant="outlined"
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                startIcon={<Inventory />}
                onClick={() => setShowCalc((v) => !v)}
                fullWidth
              >
                {showCalc ? 'Hide' : 'Show'} Material Requirements
              </Button>
              {showCalc && (
                <Box sx={{ mt: 2 }}>
                  <MaterialRequirementsTableV2
                    workOrderId={selectedStep.work_order_id}
                    step={selectedStep}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} size="large">Cancel</Button>
        <Button
          onClick={() => onComplete({ ...data, scrapped_parts: validParts })}
          variant="contained"
          color="success"
          size="large"
          startIcon={<CheckCircle />}
          data-testid="complete-submit"
          disabled={unitsEntered <= 0 && validParts.length === 0}
        >
          {unitsEntered <= 0 && validParts.length > 0 ? 'Record Scrapped Parts' : 'Record Production'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// MaterialShortageDialog — shared component (also used by the status chip)
// ---------------------------------------------------------------------------

function MaterialShortageDialog({ open, onClose, woNumber, shortages }) {
  return (
    <MaterialShortageDialogV2
      open={open}
      onClose={onClose}
      woNumber={woNumber}
      shortages={shortages}
    />
  );
}

// ---------------------------------------------------------------------------
// AdminEditDialog — maps to v2 adjust endpoint
// ---------------------------------------------------------------------------

function AdminEditDialogV2({ open, onClose, step, onSave, onRevised }) {
  const [item, setItem] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  // Plan-quantity revision (quantity_to_produce) — separate from the ledger
  // adjustment below. Works on child (sub-assembly) WOs too: the server
  // revises the WO + its own sub-tree and adjusts tree reservations (the
  // 1.99-should-be-2 netting-artifact fix).
  const [planQty, setPlanQty] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (step) {
      setItem(String(step.output_item_id || ''));
      setQuantity('');
      setReason('');
      setPlanQty(String(step.plan_quantity ?? step.quantity_to_produce ?? ''));
    }
  }, [step]);

  if (!step) return null;

  // --- Terminal View preview (mirrors v1 AdminEditWorkOrderDialog) ---------
  // Show the operator's current view of the row and what it'll look like
  // after the adjustment posts. This is identifying — not authoritative —
  // because v2 adjustments are signed ledger movements, not direct edits.
  // A positive adjustment on the output item adds to produced_qty (so
  // "completed" goes up and "remaining" goes down); negative reverses.
  const total = parseFloat(step.quantity_to_produce) || 0;
  const completed = parseFloat(step.quantity_completed) || 0;
  const remaining = Math.max(0, total - completed);
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

  const qtyNum = parseFloat(quantity);
  const adjustingOutputItem = item && Number(item) === step.output_item_id;
  const hasPreview = adjustingOutputItem && !Number.isNaN(qtyNum) && qtyNum !== 0;
  const projectedCompleted = hasPreview
    ? Math.max(0, completed + qtyNum)
    : completed;
  const projectedRemaining = Math.max(0, total - projectedCompleted);
  const projectedProgress = total > 0
    ? Math.min(100, Math.round((projectedCompleted / total) * 100))
    : 0;

  const planQtyNum = parseFloat(planQty);
  const planChanged = !Number.isNaN(planQtyNum)
    && planQtyNum !== (parseFloat(step.plan_quantity ?? step.quantity_to_produce) || 0);
  const adjustmentFilled = Boolean(quantity);
  const adjustmentValid = Boolean(item && quantity && reason.trim());

  const handleSave = async () => {
    setLoading(true);
    try {
      if (planChanged) {
        await v2.workOrders.reviseQuantity(step.work_order_id, {
          quantity: planQtyNum,
          notes: reason.trim() || 'Plan quantity revised from shop floor',
        });
        toast.success(`${step.wo_number}: plan quantity revised to ${planQtyNum}`);
        if (onRevised) onRevised();
      }
      if (adjustmentFilled) {
        await onSave(step, {
          item: Number(item),
          quantity,
          reason,
        });
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Failed to adjust');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit /> Admin Adjust: {step.wo_number}
          {step.total_steps > 1 && (
            <Chip
              label={`Step ${step.sequence}/${step.total_steps}`}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2">
            <strong>Admin Override:</strong> Posts an <code>adjustment</code> movement
            against this work order. V2's ledger is append-only — edits are recorded
            as new movements (signed), never overwrites.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">Output Item</Typography>
          <Typography variant="body1" fontWeight="bold">{step.output_item_name}</Typography>
          <Typography variant="body2" color="text.secondary">{step.output_item_code}</Typography>
        </Box>

        {/* Terminal View preview — what the operator sees on the shop floor */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }} data-testid="admin-preview">
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Terminal View {hasPreview ? '(current → after adjustment)' : '(current)'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {remaining}
                </Typography>
                {hasPreview && (
                  <>
                    <Typography variant="h6" color="text.secondary">→</Typography>
                    <Typography
                      variant="h4"
                      color={projectedRemaining === 0 ? 'success.main' : 'error.main'}
                      fontWeight="bold"
                      data-testid="admin-preview-remaining"
                    >
                      {projectedRemaining}
                    </Typography>
                  </>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                of {total} total
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={hasPreview ? projectedProgress : progress}
                  sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                  color={(hasPreview ? projectedProgress : progress) >= 100 ? 'success' : 'primary'}
                />
                <Typography variant="body2" fontWeight="bold" data-testid="admin-preview-progress">
                  {hasPreview ? projectedProgress : progress}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {hasPreview
                  ? `${completed} → ${projectedCompleted} completed`
                  : `${completed} completed`}
              </Typography>
            </Box>
          </Box>
          {!adjustingOutputItem && quantity && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
              Preview only shows when the adjustment item matches the WO output item ({step.output_item_code}).
              Adjusting a different item moves the ledger but doesn't change this row's progress.
            </Typography>
          )}
        </Paper>

        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Planned quantity to make"
            type="number"
            value={planQty}
            onChange={(e) => setPlanQty(e.target.value)}
            inputProps={{ step: 0.01, 'data-testid': 'admin-plan-qty' }}
            helperText={
              planChanged
                ? `Revises the plan ${step.plan_quantity ?? step.quantity_to_produce} → ${planQtyNum}. `
                  + 'Sub-WOs scale proportionally and material reservations adjust. '
                  + 'Cannot go below already-produced.'
                : 'The "of N total" figure. Edit to revise the plan (e.g. a 1.99 '
                  + 'that should be 2) — works on sub-assembly WOs too.'
            }
          />

          <Divider>
            <Typography variant="caption" color="text.secondary">
              Ledger adjustment (produced quantity)
            </Typography>
          </Divider>

          <TextField
            fullWidth
            label="Item ID"
            value={item}
            onChange={(e) => setItem(e.target.value)}
            inputProps={{ 'data-testid': 'admin-item' }}
            helperText={`Defaults to the WO output item (${step.output_item_code}, id=${step.output_item_id})`}
          />
          <TextField
            fullWidth
            label="Signed quantity"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ step: 0.01, 'data-testid': 'admin-qty' }}
            helperText="Positive to add to completed (label moves toward 0); negative to remove"
          />
          <TextField
            fullWidth
            label={adjustmentFilled ? 'Reason *' : 'Reason'}
            multiline
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ 'data-testid': 'admin-reason' }}
            required={adjustmentFilled}
            helperText={adjustmentFilled
              ? 'Required for a ledger adjustment'
              : 'Optional for a plan revision; required for a ledger adjustment'}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="error"
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
          disabled={loading || (adjustmentFilled ? !adjustmentValid : !planChanged)}
          data-testid="admin-submit"
        >
          {adjustmentFilled ? 'Submit Adjustment' : (planChanged ? 'Revise Plan Quantity' : 'Submit Adjustment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// DeleteDialog — maps to v2 cancel endpoint
// ---------------------------------------------------------------------------

function DeleteDialogV2({ open, onClose, step, onDelete, isStaff = false, onHardDeleted }) {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  // Hard-delete (permanent) is a staff-only secondary action. It's gated
  // behind a "reveal" step + typed-WO-number confirm because it reverses v1
  // writebacks/reservations/order-status across the whole tree.
  const [hardMode, setHardMode] = useState(false);
  const [hardConfirm, setHardConfirm] = useState('');
  const [hardLoading, setHardLoading] = useState(false);

  useEffect(() => {
    if (open) { setHardMode(false); setHardConfirm(''); setNotes(''); }
  }, [open]);

  if (!step) return null;
  const handle = async () => {
    setLoading(true);
    try {
      await onDelete(step, notes);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to cancel');
    } finally {
      setLoading(false);
    }
  };

  const hardConfirmed = hardConfirm.trim() === String(step.wo_number);
  const handleHardDelete = async () => {
    setHardLoading(true);
    try {
      // Always cascade the tree from the shop floor — a step row can be any
      // node in the tree and a single leaf delete would usually 400.
      const res = await v2.workOrders.delete(step.work_order_id, { cascade: 'tree' });
      const report = res?.data;
      toast.success(
        report?.deleted_work_orders != null
          ? `Permanently deleted ${report.deleted_work_orders} work order(s)`
          : `Permanently deleted ${step.wo_number}`,
      );
      if (onHardDeleted) onHardDeleted(report);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to delete');
    } finally {
      setHardLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning /> Cancel Work Order: {step.wo_number}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> v2 work orders aren't deleted — they're
            <code> cancelled</code>. The ledger row stays, outstanding reservations
            are released, and the WO disappears from the open-steps list.
          </Typography>
        </Alert>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Output Item</Typography>
          <Typography variant="body1" fontWeight="bold">{step.output_item_name}</Typography>
          <Typography variant="body2" color="text.secondary">{step.output_item_code}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Progress</Typography>
          <Typography variant="body1">
            {step.quantity_completed} of {step.quantity_to_produce} completed
          </Typography>
        </Box>
        <TextField
          fullWidth
          label="Cancellation notes (optional)"
          multiline
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          inputProps={{ 'data-testid': 'cancel-notes' }}
        />

        {/* Staff-only permanent-delete escalation. Cancelling is the primary
            action; this is a deliberately buried, high-friction alternative. */}
        {isStaff && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
            {!hardMode ? (
              <Button
                size="small"
                color="error"
                startIcon={<Delete />}
                onClick={() => setHardMode(true)}
                data-testid="hard-delete-reveal"
              >
                Permanently delete instead…
              </Button>
            ) : (
              <Box data-testid="hard-delete-panel">
                <Alert severity="error" icon={<ReportProblem />} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Permanent delete</strong> removes this work order and its
                    ENTIRE tree, reversing every stock writeback, reservation, and
                    order-status change. This cannot be undone.
                  </Typography>
                </Alert>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Type <strong>{step.wo_number}</strong> to confirm:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={hardConfirm}
                  onChange={(e) => setHardConfirm(e.target.value)}
                  placeholder={step.wo_number}
                  inputProps={{ 'data-testid': 'hard-delete-confirm' }}
                />
                <Button
                  sx={{ mt: 2 }}
                  variant="contained"
                  color="error"
                  startIcon={hardLoading ? <CircularProgress size={18} /> : <Delete />}
                  disabled={hardLoading || !hardConfirmed}
                  onClick={handleHardDelete}
                  data-testid="hard-delete-submit"
                >
                  Permanently delete entire tree
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading || hardLoading}>Cancel</Button>
        <Button
          onClick={handle}
          variant="contained"
          color="error"
          startIcon={loading ? <CircularProgress size={20} /> : <Warning />}
          disabled={loading || hardLoading}
          data-testid="cancel-submit"
        >
          Cancel Work Order
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ShopFloorTerminalV2() {
  const { operationCode } = useParams() || {};
  const navigate = useNavigate();
  const { user, logout } = useAuth() || {};

  const [steps, setSteps] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [operations, setOperations] = useState([]);
  const [operation, setOperation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI filters / state
  const [activeTab, setActiveTab] = useState(0);
  const [sortOrder, setSortOrder] = useState(readStoredSortOrder);
  const [groupByDate, setGroupByDate] = useState(readStoredGroupByDate);
  const [showDemand, setShowDemand] = useState(readStoredShowDemand);
  // null = the user has never toggled it — fall back to the page default
  // (ON for all-operations, OFF for per-op pages). Derived below so
  // switching stations re-evaluates the default without a remount.
  const [dueWindowChoice, setDueWindowChoice] = useState(readStoredDueWindow);
  const dueWindow = dueWindowChoice != null
    ? dueWindowChoice
    : (!operationCode || operationCode === 'all');
  const [filterDate, setFilterDate] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  // Browser-tab title shows the operation, exactly like the v1 terminal.
  usePageTitle(
    operation
      ? `${operation.operation_code}`
      : (!operationCode || operationCode === 'all')
        ? 'All'
        : operationCode,
  );

  const [viewMode, setViewMode] = useState(() => readStoredViewMode(operationCode));
  // Navigating between operations keeps this component mounted — re-read the
  // destination operation's remembered view mode.
  useEffect(() => { setViewMode(readStoredViewMode(operationCode)); }, [operationCode]);
  // Chunked rendering — how many top-level rows (steps in Individual mode,
  // consolidated groups in Consolidated) are mounted so far. Grown by an
  // idle-time effect below until everything is mounted.
  const [renderLimit, setRenderLimit] = useState(RENDER_CHUNK);
  const [completedDate, setCompletedDate] = useState(new Date());
  const [completedSearchTerm, setCompletedSearchTerm] = useState('');
  // Search bars on the Active tab. Server-side substring match.
  //
  // Two-level state so a keystroke doesn't fire a network fetch (and a full
  // re-render of the loaded rows) on every character: `searchTerm` /
  // `coNumberTerm` are the controlled input values (instant typing), and the
  // `applied*` values — debounced 400ms below — are what `loadData` actually
  // sends to the server and depends on. Only the applied values change the
  // row list.
  const [searchTerm, setSearchTerm] = useState('');
  const [coNumberTerm, setCoNumberTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [appliedCoNumberTerm, setAppliedCoNumberTerm] = useState('');

  // Dialog state
  const [selectedStep, setSelectedStep] = useState(null);
  const [productionDialog, setProductionDialog] = useState(false);
  // "Scrap a part" for one step row (ScrapComponentDialogV2).
  const [scrapPartDialog, setScrapPartDialog] = useState({ open: false, step: null });
  const [adminEditDialog, setAdminEditDialog] = useState({ open: false, step: null });
  const [undoDialog, setUndoDialog] = useState({ open: false, step: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, step: null });
  const [shortageDialog, setShortageDialog] = useState({ open: false, woNumber: '', shortages: [] });
  const [groupProductionDialog, setGroupProductionDialog] = useState({ open: false, sources: [], label: '' });
  const [orderSummaryDialog, setOrderSummaryDialog] = useState({ open: false, orderId: null });

  const loadingInProgress = useRef(false);
  const dialogOpenRef = useRef(false);

  // 7pm terminal curfew — crossing-based + visibility-gated (see header).
  useTerminalCurfew({
    user,
    onLogout: () => {
      toast.info('Shop floor terminal closes at 7pm. Logging out...');
      if (logout) logout();
    },
  });

  // Track dialog state to suppress auto-refresh
  useEffect(() => {
    dialogOpenRef.current =
      productionDialog
      || scrapPartDialog.open
      || adminEditDialog.open
      || deleteDialog.open
      || shortageDialog.open
      || groupProductionDialog.open
      || orderSummaryDialog.open;
  }, [productionDialog, scrapPartDialog.open, adminEditDialog.open, deleteDialog.open,
    shortageDialog.open, groupProductionDialog.open, orderSummaryDialog.open]);

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const loadOperations = useCallback(async () => {
    try {
      const res = await manufacturingAPI.operations.list();
      const list = res.data?.results || res.data || [];
      const sorted = [...list].sort((a, b) => naturalSort(a.operation_code, b.operation_code));
      setOperations(sorted);
      if (operationCode && operationCode !== 'all') {
        setOperation(sorted.find((op) => op.operation_code === operationCode) || null);
      } else {
        setOperation(null);
      }
    } catch (err) {
      console.error('Failed to load operations', err);
    }
  }, [operationCode]);

  const loadOpenSteps = useCallback(async (op, extra = {}) => {
    const params = {};
    if (op?.id) params.operation = op.id;
    if (extra.search) params.search = extra.search;
    if (extra.customer_order_number) params.customer_order_number = extra.customer_order_number;
    if (extra.due_window_days) params.due_window_days = extra.due_window_days;
    const res = await v2.shopFloor.list(params);
    return res.data?.results || res.data || [];
  }, []);

  const loadCompletedSteps = useCallback(async (op, date) => {
    const params = {};
    if (op?.id) params.operation = op.id;
    if (date) params.date = date.toISOString().slice(0, 10);
    const res = await v2.shopFloor.completed(params);
    return res.data?.results || res.data || [];
  }, []);

  const loadData = useCallback(async (showLoading = true) => {
    if (loadingInProgress.current) return;
    loadingInProgress.current = true;
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const res = await manufacturingAPI.operations.list();
      const opList = res.data?.results || res.data || [];
      const sortedOps = [...opList].sort((a, b) => naturalSort(a.operation_code, b.operation_code));
      setOperations(sortedOps);
      const currentOp = (operationCode && operationCode !== 'all')
        ? sortedOps.find((op) => op.operation_code === operationCode) || null
        : null;
      setOperation(currentOp);

      const [open, completed] = await Promise.all([
        loadOpenSteps(currentOp, {
          search: appliedSearchTerm.trim(),
          customer_order_number: appliedCoNumberTerm.trim(),
          due_window_days: dueWindow ? DUE_WINDOW_DAYS : undefined,
        }),
        loadCompletedSteps(currentOp, completedDate),
      ]);
      // Keep the PREVIOUS state identity when a background poll returns
      // identical data: every React.memo row then skips re-rendering, and
      // the chunked-mount reset effect below never fires — so a 30s
      // auto-refresh can't shrink the table back to the first chunk and
      // yank the scroll position around mid-scroll.
      setSteps((prev) => keepIfUnchanged(prev, open));
      setCompletedSteps((prev) => keepIfUnchanged(prev, completed));
    } catch (err) {
      console.error('Failed to load v2 shop floor data', err);
      setError(err);
    } finally {
      loadingInProgress.current = false;
      if (showLoading) setLoading(false);
    }
  }, [operationCode, completedDate, loadOpenSteps, loadCompletedSteps,
    appliedSearchTerm, appliedCoNumberTerm, dueWindow]);

  // Debounce the Active-tab search inputs onto the applied values (400ms).
  // Typing updates `searchTerm`/`coNumberTerm` instantly (controlled inputs)
  // but the fetch + row re-render only happens once typing pauses.
  useEffect(() => {
    const id = setTimeout(() => {
      setAppliedSearchTerm(searchTerm);
      setAppliedCoNumberTerm(coNumberTerm);
    }, 400);
    return () => clearTimeout(id);
  }, [searchTerm, coNumberTerm]);

  useEffect(() => { loadData(true); }, [loadData]);

  // Shop-floor text highlight rules — the SAME server-side rules the v1
  // terminal uses. HighlightBarsV2 owns loading AND admin editing; it calls
  // back here (on load and on every edit) so the memoized rows re-highlight
  // live. Identity only changes when the rules actually change.
  const [highlightCfg, setHighlightCfg] = useState(null);
  const handleHighlightRules = useCallback(
    (rows) => setHighlightCfg(splitHighlightRules(rows)),
    [],
  );

  // Auto-refresh — every 30s, only on per-op pages and not during dialogs
  useEffect(() => {
    if (!operationCode || operationCode === 'all') return undefined;
    const id = setInterval(() => {
      if (!dialogOpenRef.current) loadData(false);
    }, 30000);
    return () => clearInterval(id);
  }, [operationCode, loadData]);

  const reloadCompletedForDate = async (date) => {
    try {
      const data = await loadCompletedSteps(operation, date);
      setCompletedSteps(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load completed steps');
    }
  };

  // Admin-only xlsx of the Completed tab for the selected day/operation.
  const [exportingCompleted, setExportingCompleted] = useState(false);
  const handleExportCompleted = async () => {
    setExportingCompleted(true);
    try {
      const params = { date: completedDate.toISOString().slice(0, 10) };
      if (operation?.id) params.operation = operation.id;
      const res = await v2.shopFloor.completedExport(params);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const opSuffix = operation?.operation_code ? `_${operation.operation_code}` : '';
      link.setAttribute('download', `Completed_WOs_${params.date}${opSuffix}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      console.error('Completed export failed', err);
      toast.error('Export failed');
    } finally {
      setExportingCompleted(false);
    }
  };

  // -------------------------------------------------------------------------
  // Sort + group helpers
  // -------------------------------------------------------------------------

  const sortedSteps = useMemo(() => {
    const arr = [...steps];
    switch (sortOrder) {
      case 'item_name_asc':
        arr.sort((a, b) => {
          const na = (a.output_item_name || '').toLowerCase();
          const nb = (b.output_item_name || '').toLowerCase();
          if (na !== nb) return na.localeCompare(nb);
          return (a.sequence || 1) - (b.sequence || 1);
        });
        break;
      case 'item_name_desc':
        arr.sort((a, b) => {
          const na = (a.output_item_name || '').toLowerCase();
          const nb = (b.output_item_name || '').toLowerCase();
          if (na !== nb) return nb.localeCompare(na);
          return (a.sequence || 1) - (b.sequence || 1);
        });
        break;
      case 'urgency':
        // Mirrors v1's getUrgencyScore + adds stable tiebreakers so two rows
        // with the same urgency bucket fall back to due-date then WO# (v1
        // relies on JS sort stability, but explicitly tiebreaking gives the
        // same ordering even when the engine's sort is non-stable).
        arr.sort((a, b) => {
          const score = (s) => {
            const u = getUrgencyLevel(s);
            if (u === 'OVERDUE') return 0;
            if (u === 'URGENT') return 1;
            if (u === 'HIGH') return 3;
            return 5;
          };
          const diff = score(a) - score(b);
          if (diff !== 0) return diff;
          // Tiebreaker 1: due date ascending (null pushed to end)
          if (a.due_date && b.due_date) {
            const dd = new Date(a.due_date) - new Date(b.due_date);
            if (dd !== 0) return dd;
          } else if (a.due_date) {
            return -1;
          } else if (b.due_date) {
            return 1;
          }
          // Tiebreaker 2: WO# natural sort
          return naturalSort(a.wo_number, b.wo_number);
        });
        break;
      case 'due_date_asc':
        arr.sort((a, b) => {
          if (!a.due_date && !b.due_date) return itemNameTiebreak(a, b);
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          const dd = new Date(a.due_date) - new Date(b.due_date);
          // Same day: A–Z by item so each date section reads alphabetically.
          return dd !== 0 ? dd : itemNameTiebreak(a, b);
        });
        break;
      case 'due_date_desc':
        arr.sort((a, b) => {
          if (!a.due_date && !b.due_date) return itemNameTiebreak(a, b);
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          const dd = new Date(b.due_date) - new Date(a.due_date);
          return dd !== 0 ? dd : itemNameTiebreak(a, b);
        });
        break;
      case 'wo_number_asc':
        arr.sort((a, b) => naturalSort(a.wo_number, b.wo_number));
        break;
      case 'wo_number_desc':
        arr.sort((a, b) => naturalSort(b.wo_number, a.wo_number));
        break;
      default: break;
    }
    return arr;
  }, [steps, sortOrder]);

  // Individual-mode rows actually mounted this frame. Sliced BEFORE date
  // grouping, so banners appear as their first row mounts and the last
  // visible banner's count grows while chunks stream in (self-corrects in
  // under a second). When a specific date is picked the grouping below
  // already reduces the list to one small group — skip slicing so the
  // filtered view never flashes its "no work orders due" empty state.
  const visibleSteps = useMemo(
    () => (filterDate ? sortedSteps : sortedSteps.slice(0, renderLimit)),
    [sortedSteps, renderLimit, filterDate],
  );

  // Date banners organise the list BY DATE, so they only apply when the
  // chosen sort is date-shaped (due date / urgency) — or when a specific
  // date is filtered, because filtering renders through the banner path.
  // For item / WO# sorts the list renders FLAT: the always-ascending date
  // sections used to re-impose date order AFTER the user's sort, which made
  // the Sort-by dropdown look ignored ("sort keeps resetting"), worst in
  // consolidated view.
  const dateBannersApply = groupByDate
    && (Boolean(filterDate) || DATE_SECTION_SORTS.has(sortOrder));
  // Banner sections follow the chosen due-date direction.
  const dateSectionDir = sortOrder === 'due_date_desc' ? -1 : 1;

  const dateGroups = useMemo(() => {
    if (!dateBannersApply) return null;
    const map = {};
    for (const s of visibleSteps) {
      const key = s.due_date ? String(s.due_date).slice(0, 10) : 'no-date';
      const label = s.due_date
        ? new Date(s.due_date).toLocaleDateString('en-GB', {
          weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
        })
        : 'No Due Date';
      if (!map[key]) map[key] = { date: key, label, steps: [] };
      map[key].steps.push(s);
    }
    if (filterDate) {
      const k = filterDate.toISOString().split('T')[0];
      return map[k] ? [map[k]] : [];
    }
    const keys = Object.keys(map).sort((a, b) => {
      if (a === 'no-date') return 1;
      if (b === 'no-date') return -1;
      return dateSectionDir * a.localeCompare(b);
    });
    return keys.map((k) => map[k]);
  }, [dateBannersApply, dateSectionDir, filterDate, visibleSteps]);

  const consolidatedGroups = useMemo(() => {
    if (viewMode !== 'consolidated') return null;
    const woLike = sortedSteps.map((s) => ({
      id: s.work_order_id,
      step_id: s.step_id,
      wo_number: s.wo_number,
      output_item: s.output_item_id ?? s.output_item_code,
      output_item_code: s.output_item_code,
      output_item_name: s.output_item_name,
      due_date: s.due_date,
      quantity_to_produce: s.quantity_to_produce,
      status: s.status,
      customer_order: s.customer_order_id ?? null,
      customer_order_number: s.customer_order_number,
      customer_order_line: s.customer_order_line_id ?? null,
      line_number: s.line_number,
      steps: [{
        sequence: s.sequence,
        operation: s.operation_id ?? s.operation_code,
        operation_code: s.operation_code,
        operation_name: s.operation_name,
      }],
    }));
    // sortedSteps already carries the user's "Sort by" ordering; the groups
    // inherit it (see the ordering contract in groupForConsolidatedView).
    return groupForConsolidatedView(woLike, {
      groupByDate,
      dateDescending: sortOrder === 'due_date_desc',
    });
  }, [viewMode, sortedSteps, groupByDate, sortOrder]);

  // Consolidated groups are built from the FULL row set above (a group's
  // aggregates — Need Now, progress, order chips — must never show partial
  // totals mid-stream) and sliced per whole group for mounting. Same
  // filterDate exception as visibleSteps.
  const visibleConsolidatedGroups = useMemo(() => {
    if (!consolidatedGroups) return null;
    return filterDate ? consolidatedGroups : consolidatedGroups.slice(0, renderLimit);
  }, [consolidatedGroups, renderLimit, filterDate]);

  // Reset to the first chunk whenever the row SET or its ordering changes.
  // Keyed on the ordered step ids, not the steps array identity: a refresh
  // that returns the same rows (even with updated per-row fields) must not
  // unmount everything below the first chunk — that shrinks the page under
  // the operator and makes scrolling jump.
  const stepsSignature = useMemo(
    () => steps.map((s) => s.step_id).join('|'),
    [steps],
  );
  useEffect(() => {
    setRenderLimit(RENDER_CHUNK);
  }, [stepsSignature, sortOrder, viewMode, groupByDate]);

  // Stream the next chunk during idle time until everything is mounted.
  const totalTopRows = viewMode === 'consolidated' && consolidatedGroups
    ? consolidatedGroups.length
    : sortedSteps.length;
  const remainingToMount = filterDate ? 0 : Math.max(0, totalTopRows - renderLimit);
  const isAllOps = !operationCode || operationCode === 'all';
  // All-operations: automatic streaming stops at the cap; the operator adds
  // chunks explicitly via the Show-more button below.
  const autoStreamCapped = isAllOps && remainingToMount > 0 && renderLimit >= ALL_OPS_RENDER_CAP;
  useEffect(() => {
    if (remainingToMount <= 0) return undefined;
    if (isAllOps && renderLimit >= ALL_OPS_RENDER_CAP) return undefined;
    // requestIdleCallback keeps the streaming off the critical path; its
    // timeout guarantees progress even if the thread stays busy. Safari
    // has no requestIdleCallback — fall back to a short timeout.
    const idle = window.requestIdleCallback
      ? (fn) => window.requestIdleCallback(fn, { timeout: 200 })
      : (fn) => window.setTimeout(fn, 50);
    const cancel = window.cancelIdleCallback
      ? (id) => window.cancelIdleCallback(id)
      : (id) => window.clearTimeout(id);
    const id = idle(() => setRenderLimit((l) => l + RENDER_CHUNK));
    return () => cancel(id);
  }, [remainingToMount, isAllOps, renderLimit]);

  // Look up the original step record (with all the rich shop-floor fields)
  // for a group source. Keyed by STEP id — a multi-step WO has several rows
  // sharing one work_order_id, and a WO-keyed map made every group resolve
  // to the same (last) step, so step 1's row showed step 2's blocked state.
  // The WO-keyed map remains as a fallback for sources without a step_id.
  const stepsByStepId = useMemo(() => {
    const map = new Map();
    for (const s of sortedSteps) {
      if (s && s.step_id != null) map.set(s.step_id, s);
    }
    return map;
  }, [sortedSteps]);
  const stepsByWorkOrderId = useMemo(() => {
    const map = new Map();
    for (const s of sortedSteps) {
      if (s && s.work_order_id != null) map.set(s.work_order_id, s);
    }
    return map;
  }, [sortedSteps]);

  // Header pill counts — single pass over steps, memoized so they don't
  // recompute (three separate .filter() sweeps over up to 500 rows) on every
  // unrelated re-render such as a search keystroke or dialog open/close.
  const { overdueCount, multiStepCount } = useMemo(() => {
    let overdue = 0;
    let multi = 0;
    for (const s of steps) {
      if (getUrgencyLevel(s) === 'OVERDUE') overdue += 1;
      if (s.total_steps > 1) multi += 1;
    }
    return { overdueCount: overdue, multiStepCount: multi };
  }, [steps]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  // Stable callbacks — these are passed to the memoized StepRowV2 /
  // GroupRowV2 rows; identity stability is what lets React.memo skip
  // re-rendering 500 rows when one of them is toggled.
  const toggleRow = useCallback((id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const openAdminEdit = useCallback((step) => setAdminEditDialog({ open: true, step }), []);
  const openDelete = useCallback((step) => setDeleteDialog({ open: true, step }), []);
  const openOrderSummary = useCallback(
    (orderId) => setOrderSummaryDialog({ open: true, orderId }), [],
  );
  const openGroupStart = useCallback(
    (sources, label) => setGroupProductionDialog({ open: true, sources, label }), [],
  );

  const handleViewModeChange = (_e, mode) => {
    if (mode == null) return;
    setViewMode(mode);
    writeStoredViewMode(operationCode, mode);
  };

  const handleSortChange = (newSort) => {
    setSortOrder(newSort);
    writeStoredSortOrder(newSort);
  };
  const handleGroupByDateToggle = () => {
    const next = !groupByDate;
    setGroupByDate(next);
    writeStoredGroupByDate(next);
    if (!next) setFilterDate(null);
  };
  const handleDueWindowToggle = () => {
    const next = !dueWindow;
    setDueWindowChoice(next);
    writeStoredDueWindow(next);
    // dueWindow is a loadData dependency, so flipping it triggers a refetch
    // (with/without due_window_days=14) automatically.
  };
  const handleShowDemandToggle = () => {
    const next = !showDemand;
    setShowDemand(next);
    writeStoredShowDemand(next);
  };
  const handleFilterDateChange = (date) => {
    setFilterDate(date);
    if (date && !groupByDate) {
      setGroupByDate(true);
      writeStoredGroupByDate(true);
    }
  };

  const handleStart = useCallback((step) => {
    // Partial release: a step is locked only until its predecessor has
    // completed at least one unit; after that it can make exactly what the
    // predecessor has released. The backend enforces both ends — this is
    // the friendly message layer.
    const prevSeq = step.depends_on_sequence ?? ((step.sequence || 1) - 1);
    if (step.total_steps > 1 && !step.can_start) {
      toast.error(
        `Step ${prevSeq} hasn't completed any units yet — step ${step.sequence} is locked until it has.`,
      );
      return;
    }
    if (
      step.total_steps > 1
      && step.depends_on_id != null
      && step.qty_available_from_previous_step != null
    ) {
      const avail = parseFloat(step.qty_available_from_previous_step) || 0;
      toast.info(`Can make ${avail} — released by step ${prevSeq}.`);
    }
    setSelectedStep(step);
    setProductionDialog(true);
  }, []);

  // Post the dialog's "scrapped parts" (components remade on the spot) via
  // the WO scrap endpoint — raw material out of stock, WO untouched. Runs
  // AFTER a successful sign-off so a blocked sign-off records nothing.
  const recordScrappedParts = async (parts) => {
    const done = [];
    for (const part of parts) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await v2.workOrders.scrap(selectedStep.work_order_id, {
          item: part.item_id,
          quantity: String(parseFloat(part.quantity)),
          step: selectedStep.step_id,
          notes: (part.reason || '').trim() || `Scrapped at step ${selectedStep.sequence} sign-off`,
        });
        done.push(`${parseFloat(part.quantity)} × ${res.data?.item_code || part.item_id}`);
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.error || 'Failed to record a scrapped part');
      }
    }
    if (done.length) toast.success(`Scrapped parts recorded: ${done.join(', ')} — material taken from stock`);
  };

  const handleComplete = async (formData) => {
    if (!selectedStep) return;
    const qtyGood = parseFloat(formData.quantity_completed) || 0;
    const qtyScrap = parseFloat(formData.quantity_scrapped) || 0;
    const parts = formData.scrapped_parts || [];
    // Scrapped parts only (no units made or written off): nothing to sign
    // off, just record the parts.
    if (qtyGood + qtyScrap <= 0 && parts.length > 0) {
      await recordScrappedParts(parts);
      setProductionDialog(false);
      loadData(true);
      return;
    }
    // Scrap is on top of the good count ("made 6, scrapped 2" = 8 attempts),
    // so a scrap-only entry is valid — the whole run may have failed.
    if (qtyGood < 0 || qtyScrap < 0 || qtyGood + qtyScrap <= 0) {
      toast.error('Please enter quantity completed or scrapped');
      return;
    }

    // Multi-step hard block — mirrors v1 handleCompleteProduction
    // (ShopFloorTerminal.js line ~3293): can't record more than the prior
    // step has released. The backend's depends_on machinery enforces this
    // separately, but we surface it here too so the operator gets a clear
    // message instead of a 4xx.
    if (
      selectedStep.total_steps > 1
      && selectedStep.depends_on_id != null
      && selectedStep.qty_available_from_previous_step != null
    ) {
      const availFromPrev = parseFloat(selectedStep.qty_available_from_previous_step) || 0;
      if (qtyGood + qtyScrap > availFromPrev) {
        toast.error(
          `Step ${(selectedStep.sequence || 1) - 1} has released ${availFromPrev} unit(s) — `
          + `you can make ${availFromPrev}.`,
        );
        return;
      }
    }

    // Pre-flight material check (mirrors v1) — shortages BLOCK production
    // (2026-07-05): the shortage dialog lists what's missing and nothing is
    // recorded until the material is genuinely available. Covers the good
    // units; the server plans replacement material for scrapped units and
    // checks those itself.
    try {
      const check = qtyGood > 0
        ? await v2.workOrders.materialAvailability(selectedStep.work_order_id, {
          quantity: qtyGood,
          step: selectedStep.step_id,
        })
        : { data: {} };
      if (check.data?.shortages?.length > 0) {
        setShortageDialog({
          open: true,
          woNumber: selectedStep.wo_number,
          shortages: check.data.shortages,
        });
        return;
      }
      const overprod = check.data?.warnings?.find((w) => w.type === 'overproduction');
      if (overprod) {
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
          `Overproduction detected\n\nYou're making ${qtyGood} units, but only ${
            (parseFloat(selectedStep.quantity_to_produce) || 0)
            - (parseFloat(selectedStep.quantity_completed) || 0)
          } are needed.\n\n`
          + `This will create ${overprod.extra_units} extra units in stock.\n\n`
          + 'Do you want to proceed?',
        );
        if (!ok) {
          toast.info('Production cancelled by user');
          return;
        }
      }
    } catch (err) {
      console.error('Material check failed', err);
      toast.error('Cannot verify material availability — production blocked for safety');
      return;
    }

    try {
      // No separate start call: complete starts a planned step itself and
      // issues materials for just the units recorded (partial sign-off). An
      // explicit start would issue — and demand material for — the whole WO.
      await v2.workOrders.completeStep(selectedStep.work_order_id, selectedStep.step_id, {
        quantity_good: qtyGood,
        quantity_scrapped: qtyScrap,
      });
      // The backend writeback only fires on the LAST step. Surface the
      // allocation hint to the operator so they know v1 shipping / the
      // customer-order page have been updated for this run.
      const isLastStep = selectedStep.sequence >= selectedStep.total_steps;
      const coNumber = selectedStep.customer_order_number;
      if (qtyGood <= 0) {
        toast.success(`Recorded ${qtyScrap} scrapped — replacements still to make`);
      } else if (!isLastStep) {
        toast.success(
          `${qtyGood} units completed on Step ${selectedStep.sequence} — released to Step ${selectedStep.sequence + 1}`,
        );
      } else if (selectedStep.total_steps > 1) {
        toast.success(
          coNumber
            ? `Completed final step! ${qtyGood} finished units added to stock — allocated to CO ${coNumber}.`
            : `Completed final step! ${qtyGood} finished units added to stock.`,
        );
      } else {
        toast.success(
          coNumber
            ? `Recorded ${qtyGood} units completed — allocated to CO ${coNumber}`
            : `Recorded ${qtyGood} units completed`,
        );
      }
      if (parts.length) await recordScrappedParts(parts);
      setProductionDialog(false);
      loadData(true);
    } catch (err) {
      console.error(err);
      // {error} carries gate messages ("can make X", cancelled, 2× cap,
      // shortages); {detail} carries DRF-level errors.
      toast.error(
        err?.response?.data?.error
        || err?.response?.data?.detail
        || 'Failed to complete step',
      );
    }
  };

  const openScrapPart = (step) => setScrapPartDialog({ open: true, step });

  // The scrap strip mirrors the queue's server-side filters so its
  // suggestions come from exactly the rows on screen.
  const scrapFilters = useMemo(() => ({
    operation: operation?.id || undefined,
    search: appliedSearchTerm || undefined,
    customer_order_number: appliedCoNumberTerm || undefined,
    due_window_days: dueWindow ? DUE_WINDOW_DAYS : undefined,
  }), [operation, appliedSearchTerm, appliedCoNumberTerm, dueWindow]);

  const handleAdminAdjust = async (step, payload) => {
    try {
      await v2.workOrders.adjust(step.work_order_id, payload);
      toast.success('Adjustment posted');
      loadData(false);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handleCancelWO = async (step, notes) => {
    try {
      await v2.workOrders.cancel(step.work_order_id, { notes });
      toast.success(`Cancelled ${step.wo_number}`);
      loadData(false);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  // -------------------------------------------------------------------------
  // Render: row + tables
  // -------------------------------------------------------------------------

  // Memoized rows — see components/ShopFloorRowsV2.jsx. Only primitive /
  // stable props cross this boundary, so expanding one row no longer
  // re-renders the other ~500 (the cause of the expansion lag).
  const materialAware = operationCode !== 'all';
  const isStaff = !!user?.is_staff;

  const renderRow = (s) => (
    <StepRowV2
      key={s.step_id}
      s={s}
      isExpanded={expandedRows.has(s.step_id)}
      materialAware={materialAware}
      isStaff={isStaff}
      showDemand={showDemand}
      highlightCfg={highlightCfg}
      onToggle={toggleRow}
      onStart={handleStart}
      onAdminEdit={openAdminEdit}
      onDelete={openDelete}
      onOrderSummary={openOrderSummary}
      onScrap={openScrapPart}
    />
  );


  // -------------------------------------------------------------------------
  // Consolidated row — same 9-column table as Individual, one row per group.
  //
  // Aggregation rules (documented inline because the parent agent will read
  // these and the task report references them):
  //
  //   - Urgency:        OVERDUE > URGENT > HIGH > NORMAL. Computed by taking
  //                     the earliest concrete due_date in the group; OVERDUE
  //                     "wins" if any single source is overdue.
  //   - Need Now:       sum across sources of (quantity_to_produce -
  //                     quantity_completed), floored at 0.
  //   - Total Demand:   sum of (line_quantity_ordered - line_quantity_shipped)
  //                     across sources. Shows "across N orders" — N is the
  //                     count of distinct customer-order numbers in the group.
  //   - Material Status: if any underlying source step has a shortage, show
  //                     a single "N missing" chip (sum across sources). We
  //                     don't fan-out one chip per source — that defeats the
  //                     point of consolidation. The chip is wired to the
  //                     first source step's work order so its async fetch
  //                     stays correct; if multiple sources have shortages the
  //                     surfaced count is the first source's count, NOT a
  //                     true sum, because each MaterialStatusChipV2 issues
  //                     its own availability call. Pragmatic compromise: the
  //                     chip still flags "something's short", and the user
  //                     drills into the mini-rows via Start to see per-WO
  //                     detail. (Alternative: a dedicated group-level
  //                     availability endpoint; not built yet.)
  //   - Due Date:       when groupByDate=true the group has a single date
  //                     (same colouring as individual). When groupByDate=false
  //                     show the earliest date plus "+N more dates" when
  //                     multiple distinct dates are present.
  //   - Progress:       sum(quantity_completed) / sum(quantity_to_produce)
  //                     across sources, as a LinearProgress + percentage.
  //   - Actions:        Start expands the row into per-source mini-rows.
  //                     Scrap opens the scrap dialog for the FIRST source
  //                     step (group-level scrap doesn't really have a clear
  //                     meaning across multiple WOs — operators record scrap
  //                     against the individual mini-row Scrap button when
  //                     they want precision). Admin Edit / Delete are hidden
  //                     at the group level and surface on the expanded
  //                     mini-rows.
  // -------------------------------------------------------------------------

  const groupExpansionKey = (g) => `group:${g.key}`;

  // Source step records per group key — memoized so the array identity is
  // stable across expansion toggles (a requirement for GroupRowV2's memo).
  const groupSourcesByKey = useMemo(() => {
    const m = new Map();
    for (const g of consolidatedGroups || []) {
      m.set(g.key, (g.sources || [])
        .map((src) => (
          (src.step_id != null ? stepsByStepId.get(src.step_id) : null)
          || stepsByWorkOrderId.get(src.work_order_id)
        ))
        .filter(Boolean));
    }
    return m;
  }, [consolidatedGroups, stepsByStepId, stepsByWorkOrderId]);

  const renderGroupRow = (g) => (
    <GroupRowV2
      key={groupExpansionKey(g)}
      g={g}
      sources={groupSourcesByKey.get(g.key) || []}
      isExpanded={expandedRows.has(groupExpansionKey(g))}
      expansionId={groupExpansionKey(g)}
      groupByDate={groupByDate}
      materialAware={materialAware}
      isStaff={isStaff}
      showDemand={showDemand}
      highlightCfg={highlightCfg}
      onToggle={toggleRow}
      onStart={handleStart}
      onAdminEdit={openAdminEdit}
      onDelete={openDelete}
      onOrderSummary={openOrderSummary}
      onGroupStart={openGroupStart}
      onScrap={openScrapPart}
    />
  );


  // Consolidated groups bucketed by their due_date — used to render the same
  // OVERDUE/TODAY/TOMORROW banners individual mode shows when Group-by-Date
  // is on. When groupByDate=false the banners don't apply (groups can span
  // multiple dates) so callers fall through to a flat list.
  const consolidatedDateGroups = useMemo(() => {
    if (!visibleConsolidatedGroups || !dateBannersApply) return null;
    const map = {};
    for (const g of visibleConsolidatedGroups) {
      const key = g.due_date ? String(g.due_date).slice(0, 10) : 'no-date';
      const label = g.due_date
        ? new Date(g.due_date).toLocaleDateString('en-GB', {
          weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
        })
        : 'No Due Date';
      if (!map[key]) map[key] = { date: key, label, groups: [] };
      map[key].groups.push(g);
    }
    if (filterDate) {
      const k = filterDate.toISOString().split('T')[0];
      return map[k] ? [map[k]] : [];
    }
    const keys = Object.keys(map).sort((a, b) => {
      if (a === 'no-date') return 1;
      if (b === 'no-date') return -1;
      return dateSectionDir * a.localeCompare(b);
    });
    return keys.map((k) => map[k]);
  }, [visibleConsolidatedGroups, dateBannersApply, dateSectionDir, filterDate]);

  // -------------------------------------------------------------------------
  // Active tab body
  // -------------------------------------------------------------------------

  const renderActiveBody = () => {
    if (loading) return <LinearProgress />;
    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="error-state">
          Failed to load shop floor steps.
        </Alert>
      );
    }
    if (steps.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="empty-state">
          <Typography variant="h6">No work orders available</Typography>
          <Typography>
            No v2 steps are released for this operation. Check with your supervisor
            or try refreshing.
          </Typography>
        </Alert>
      );
    }

    const isConsolidated = viewMode === 'consolidated' && consolidatedGroups;

    return (
      <TableContainer>
        <Table
          data-testid="step-list"
          data-view-mode={isConsolidated ? 'consolidated' : 'individual'}
          // Tighter horizontal padding than MUI's 16px default — with the
          // optional Total Demand column visible the 9 columns otherwise
          // push Actions (the Start buttons) off typical screens.
          sx={{ '& th, & td': { px: 1.25 } }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Work Order & Urgency</TableCell>
              <TableCell>What to Make</TableCell>
              <TableCell>Need Now</TableCell>
              {showDemand && <TableCell>Total Demand</TableCell>}
              <TableCell>Material Status</TableCell>
              <TableCell>Customer Order #</TableCell>
              <TableCell>Order Notes</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              // Date-group banner — shared between Individual and Consolidated
              // when Group-by-Date is on. `rows` is an array of either step
              // records or group records (the caller picks the render fn).
              const renderBannerSection = (group, gi, rows, rowRenderer) => {
                const color = getDateGroupColor(group.date);
                const days = group.date && group.date !== 'no-date'
                  ? Math.ceil((new Date(group.date) - new Date()) / (1000 * 60 * 60 * 24))
                  : null;
                const isUrgent = color === 'error.main';
                const isWarning = color === 'warning.main';
                const urgencyLabel = isUrgent ? (days !== null && days < 0 ? 'OVERDUE' : 'TODAY')
                  : isWarning ? 'TOMORROW' : null;
                const bannerBg = isUrgent ? 'error.main'
                  : isWarning ? 'warning.main'
                  : color === 'info.main' ? 'info.main'
                  : 'grey.700';
                const bannerText = isWarning ? 'warning.contrastText' : '#fff';
                return (
                  <React.Fragment key={group.date || group.label}>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0, border: 'none', height: gi > 0 ? 24 : 4, background: 'transparent' }} />
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0, border: 'none' }}>
                        <Box
                          data-testid={`date-banner-${group.date}`}
                          sx={{
                            bgcolor: bannerBg,
                            color: bannerText,
                            py: 1.2,
                            px: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            borderRadius: '6px 6px 0 0',
                          }}
                        >
                          <CalendarMonth sx={{ fontSize: '1.5rem' }} />
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '0.02em' }}>
                            {group.label}
                          </Typography>
                          {urgencyLabel && (
                            <Chip
                              label={urgencyLabel}
                              size="small"
                              sx={{
                                height: 22, fontSize: '0.7rem', fontWeight: 800,
                                bgcolor: 'rgba(255,255,255,0.25)', color: bannerText,
                                letterSpacing: '0.05em',
                              }}
                            />
                          )}
                          <Box sx={{ flex: 1 }} />
                          <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 500 }}>
                            {rows.length} work order{rows.length !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {rows.map(rowRenderer)}
                  </React.Fragment>
                );
              };

              if (isConsolidated) {
                if (consolidatedDateGroups) {
                  if (consolidatedDateGroups.length === 0 && filterDate) {
                    return (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">
                            No work orders due on {filterDate.toLocaleDateString('en-GB', {
                              weekday: 'long', day: '2-digit', month: 'short', year: 'numeric',
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return consolidatedDateGroups.map((dg, gi) =>
                    renderBannerSection(dg, gi, dg.groups, renderGroupRow));
                }
                return (visibleConsolidatedGroups || []).map(renderGroupRow);
              }

              if (dateGroups) {
                if (dateGroups.length === 0 && filterDate) {
                  return (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          No work orders due on {filterDate.toLocaleDateString('en-GB', {
                            weekday: 'long', day: '2-digit', month: 'short', year: 'numeric',
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }
                return dateGroups.map((group, gi) =>
                  renderBannerSection(group, gi, group.steps, renderRow));
              }

              return visibleSteps.map(renderRow);
            })()}
            {remainingToMount > 0 && (
              <TableRow data-testid="render-stream-row">
                <TableCell
                  colSpan={showDemand ? 10 : 9}
                  sx={{ textAlign: 'center', py: 2, border: 'none' }}
                >
                  {autoStreamCapped ? (
                    <Button
                      data-testid="show-more-rows"
                      size="small"
                      variant="outlined"
                      onClick={() => setRenderLimit((l) => l + RENDER_CHUNK)}
                    >
                      Show {Math.min(RENDER_CHUNK, remainingToMount)} more
                      &nbsp;({remainingToMount} not shown)
                    </Button>
                  ) : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading {remainingToMount} more…
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // -------------------------------------------------------------------------
  // Completed tab body
  // -------------------------------------------------------------------------

  const filteredCompleted = completedSteps.filter((s) => {
    if (!completedSearchTerm) return true;
    const q = completedSearchTerm.toLowerCase();
    return (
      (s.wo_number || '').toLowerCase().includes(q)
      || (s.output_item_name || '').toLowerCase().includes(q)
      || (s.output_item_code || '').toLowerCase().includes(q)
    );
  });

  const renderCompletedBody = () => {
    if (loading) return <LinearProgress />;
    if (filteredCompleted.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="completed-empty">
          <Typography variant="h6">No completed steps found</Typography>
          <Typography>
            {completedSearchTerm
              ? 'Try adjusting your search.'
              : `No steps completed on ${completedDate.toLocaleDateString()}.`}
          </Typography>
        </Alert>
      );
    }
    return (
      <TableContainer>
        <Table data-testid="completed-list">
          <TableHead>
            <TableRow>
              <TableCell>Work Order</TableCell>
              <TableCell>Output Item</TableCell>
              <TableCell>Day's Output</TableCell>
              <TableCell>Day's Scrap</TableCell>
              <TableCell>Completed By</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCompleted.map((s) => {
              const completed = new Date(s.completed_at || s.started_at);
              return (
                <TableRow key={s.step_id} hover data-testid={`completed-row-${s.step_id}`}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body1" fontWeight="bold">
                        {s.wo_number}
                      </Typography>
                      <StepIndicator step={s} />
                    </Box>
                    <Chip
                      label={s.wo_status === 'completed' ? 'COMPLETED' : 'IN PROGRESS'}
                      size="small"
                      color={s.wo_status === 'completed' ? 'success' : 'warning'}
                      icon={s.wo_status === 'completed' ? <CheckCircle /> : <Build />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">{s.output_item_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{s.output_item_code}</Typography>
                    <RootItemsDisplayV2 step={s} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      {parseFloat(s.quantity_completed || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      of {parseFloat(s.quantity_to_produce)} total
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {parseFloat(s.quantity_scrapped || 0) > 0 ? (
                      <Typography variant="body1" color="error.main" fontWeight="bold">
                        {parseFloat(s.quantity_scrapped)}
                      </Typography>
                    ) : <Typography variant="body2" color="text.secondary">-</Typography>}
                  </TableCell>
                  <TableCell data-testid={`completed-by-${s.step_id}`}>
                    <Typography variant="body2">{s.completed_by || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{completed.toLocaleDateString()}</Typography>
                    <Typography variant="caption" color="text.secondary">{completed.toLocaleTimeString()}</Typography>
                    {isToday(s.completed_at) && (
                      <Chip label="Today" size="small" color="info" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={s.notes}
                    >
                      {s.notes || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {user?.is_staff ? (
                        <Tooltip
                          title="Post a signed adjustment movement against this WO. V2 ledger is append-only — the original completion stays in history."
                          arrow
                        >
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<Edit />}
                            data-testid={`btn-admin-${s.step_id}`}
                            onClick={() => setAdminEditDialog({ open: true, step: s })}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Admins only — edits are posted as signed ledger adjustments" arrow>
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Edit />}
                              disabled
                            >
                              Edit
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {user?.is_staff && s.wo_status === 'completed' && (
                        <Tooltip title="Put this work order back to planned — reverses its stock, materials and allocation" arrow>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Undo />}
                            data-testid={`btn-undo-${s.step_id}`}
                            onClick={() => setUndoDialog({ open: true, step: s })}
                          >
                            Undo
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header card — mirrors v1 */}
      <Card sx={{ mb: 3, border: '2px solid #1976d2' }}>
        <CardContent>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Build color="primary" />
                Shop Floor Terminal
                <GenerationModeBadge />
                {user?.is_staff && (
                  <Chip label="ADMIN" size="small" color="error" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {operation
                  ? `${operation.operation_code} - ${operation.name}`
                  : operationCode === 'all'
                    ? 'All Operations'
                    : operationCode
                      ? `Operation ${operationCode}`
                      : 'All Operations'}
              </Typography>
              {operation?.work_instructions && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Instructions:</strong> {operation.work_instructions}
                  </Typography>
                </Alert>
              )}
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  size="small"
                  data-testid="view-mode-toggle"
                >
                  <ToggleButton value="individual" data-testid="view-mode-individual">Individual</ToggleButton>
                  <ToggleButton value="consolidated" data-testid="view-mode-consolidated">Consolidated</ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => loadData(true)}
                  disabled={loading}
                  size="small"
                >
                  Refresh
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Station Quick Switch — mirrors v1 */}
      {!loading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Station Quick Switch</Typography>
            <Grid container spacing={1}>
              <Grid item>
                <Button
                  data-testid="station-all"
                  variant={!operationCode || operationCode === 'all' ? 'contained' : 'outlined'}
                  onClick={() => navigate('/manufacturing/v2/shop-floor')}
                  size="small"
                >
                  All Operations
                </Button>
              </Grid>
              {operations.map((op) => (
                <Grid item key={op.id}>
                  <Button
                    data-testid={`station-${op.operation_code}`}
                    variant={operationCode === op.operation_code ? 'contained' : 'outlined'}
                    onClick={() => navigate(`/manufacturing/v2/shop-floor/${op.operation_code}`)}
                    size="small"
                  >
                    {op.operation_code} - {op.name}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Text-highlight rules — admin editors + summary chips (v1 parity) */}
      <HighlightBarsV2 isStaff={isStaff} onRulesChanged={handleHighlightRules} />

      <Card>
        <CardContent>
          {/* Tabs — Active vs Completed */}
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              data-testid="tab-active"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment />
                  Active Work Orders
                  <Chip label={steps.length} size="small" color="primary" />
                </Box>
              }
            />
            <Tab
              data-testid="tab-completed"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History />
                  Completed
                  <Chip label={completedSteps.length} size="small" color="success" />
                </Box>
              }
            />
          </Tabs>

          {activeTab === 0 && (
            <>
              {/* Scrap strip — scrap any item from stock, with suggestions
                  drawn from the work orders on this screen. */}
              <ScrapStripV2 filters={scrapFilters} onRecorded={() => loadData(false)} />

              {/* Search bars — server-side substring filtering against
                  WO number / item code / item name, and a dedicated CO
                  number box. Mirrors the same pair on the v2 Work Orders
                  page. */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label="Search WO #, item code, or item name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="e.g. WO2-001 · SBC25000 · Folding Table"
                  inputProps={{ 'data-testid': 'shop-floor-search' }}
                  sx={{ flex: 2, minWidth: 280 }}
                />
                <TextField
                  size="small"
                  label="Customer order number"
                  value={coNumberTerm}
                  onChange={(e) => setCoNumberTerm(e.target.value)}
                  placeholder="e.g. SO-001"
                  inputProps={{ 'data-testid': 'shop-floor-search-co-number' }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </Box>

              <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                mb: 2, flexWrap: 'wrap', gap: 1,
              }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment />
                  Available Work Orders ({steps.length})
                  {overdueCount > 0 && (
                    <Chip
                      label={`${overdueCount} URGENT`}
                      color="error"
                      size="small"
                      icon={<Warning />}
                    />
                  )}
                  {multiStepCount > 0 && (
                    <Chip
                      label={`${multiStepCount} Multi-Step`}
                      color="info"
                      size="small"
                      icon={<LinkIcon />}
                      variant="outlined"
                    />
                  )}
                </Typography>

                <TextField
                  select
                  size="small"
                  value={sortOrder}
                  onChange={(e) => handleSortChange(e.target.value)}
                  label="Sort by"
                  sx={{ minWidth: 200 }}
                  SelectProps={{ native: true }}
                  inputProps={{ 'data-testid': 'sort-select' }}
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </TextField>
                <Box sx={{
                  display: 'flex', alignItems: 'center',
                  border: 1,
                  borderColor: groupByDate ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'border-color 0.2s',
                }}>
                  <Button
                    startIcon={<CalendarMonth />}
                    onClick={handleGroupByDateToggle}
                    size="small"
                    variant={groupByDate ? 'contained' : 'text'}
                    color={groupByDate ? 'primary' : 'inherit'}
                    sx={{
                      borderRadius: 0,
                      textTransform: 'none',
                      px: 1.5,
                      minHeight: 36,
                      fontWeight: groupByDate ? 600 : 400,
                      ...(groupByDate ? {} : { color: 'text.secondary' }),
                    }}
                    data-testid="toggle-group-by-date"
                  >
                    {groupByDate ? 'By Date' : 'Group by Date'}
                  </Button>
                  {groupByDate && (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                      <DatePicker
                        value={filterDate}
                        onChange={handleFilterDateChange}
                        slotProps={{
                          textField: {
                            size: 'small',
                            placeholder: 'All dates',
                            sx: {
                              width: 150,
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                              '& .MuiInputBase-root': { borderRadius: 0, pl: 0.5 },
                              '& .MuiInputBase-input': { py: '6px', fontSize: '0.85rem' },
                            },
                          },
                          field: { clearable: true },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Box>
                <Tooltip title="Only load work orders that are overdue or due in the next 14 days. Demand totals still cover all dates.">
                  <Box sx={{
                    display: 'flex', alignItems: 'center',
                    border: 1,
                    borderColor: dueWindow ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'border-color 0.2s',
                  }}>
                    <Button
                      startIcon={<Warning />}
                      onClick={handleDueWindowToggle}
                      size="small"
                      variant={dueWindow ? 'contained' : 'text'}
                      color={dueWindow ? 'primary' : 'inherit'}
                      sx={{
                        borderRadius: 0,
                        textTransform: 'none',
                        px: 1.5,
                        minHeight: 36,
                        fontWeight: dueWindow ? 600 : 400,
                        ...(dueWindow ? {} : { color: 'text.secondary' }),
                      }}
                      data-testid="toggle-due-window"
                    >
                      Overdue + 2 wks
                    </Button>
                  </Box>
                </Tooltip>
                <Tooltip title="Show the Total Demand column (open demand per item, broken down by when it's due)">
                  <Box sx={{
                    display: 'flex', alignItems: 'center',
                    border: 1,
                    borderColor: showDemand ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'border-color 0.2s',
                  }}>
                    <Button
                      startIcon={<Timeline />}
                      onClick={handleShowDemandToggle}
                      size="small"
                      variant={showDemand ? 'contained' : 'text'}
                      color={showDemand ? 'primary' : 'inherit'}
                      sx={{
                        borderRadius: 0,
                        textTransform: 'none',
                        px: 1.5,
                        minHeight: 36,
                        fontWeight: showDemand ? 600 : 400,
                        ...(showDemand ? {} : { color: 'text.secondary' }),
                      }}
                      data-testid="toggle-show-demand"
                    >
                      Demand
                    </Button>
                  </Box>
                </Tooltip>
              </Box>
              {renderActiveBody()}
            </>
          )}

          {activeTab === 1 && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  placeholder="Search work orders..."
                  size="small"
                  value={completedSearchTerm}
                  onChange={(e) => setCompletedSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                  inputProps={{ 'data-testid': 'completed-search' }}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <DatePicker
                    label="Date"
                    value={completedDate}
                    onChange={(d) => {
                      if (d) {
                        setCompletedDate(d);
                        reloadCompletedForDate(d);
                      }
                    }}
                    maxDate={new Date()}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 170 } } }}
                  />
                </LocalizationProvider>
                <Button
                  size="small"
                  variant={completedDate.toDateString() === new Date().toDateString() ? 'contained' : 'outlined'}
                  onClick={() => {
                    const today = new Date();
                    setCompletedDate(today);
                    reloadCompletedForDate(today);
                  }}
                  data-testid="completed-today"
                >
                  Today
                </Button>
                {user?.is_staff && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleExportCompleted}
                    disabled={exportingCompleted}
                    data-testid="completed-export"
                  >
                    {exportingCompleted ? 'Exporting...' : 'Export'}
                  </Button>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  Showing {filteredCompleted.length} of {completedSteps.length} steps for {completedDate.toLocaleDateString()}
                </Typography>
              </Box>
              {renderCompletedBody()}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <EnhancedProductionDialogV2
        open={productionDialog}
        onClose={() => setProductionDialog(false)}
        selectedStep={selectedStep}
        onComplete={handleComplete}
      />

      <MaterialShortageDialog
        open={shortageDialog.open}
        onClose={() => setShortageDialog({ open: false, woNumber: '', shortages: [] })}
        woNumber={shortageDialog.woNumber}
        shortages={shortageDialog.shortages}
      />

      <GroupProductionDialogV2
        open={groupProductionDialog.open}
        onClose={() => setGroupProductionDialog({ open: false, sources: [], label: '' })}
        sources={groupProductionDialog.sources}
        groupLabel={groupProductionDialog.label}
        onDone={() => loadData(false)}
      />

      <OrderSummaryDialogV2
        open={orderSummaryDialog.open}
        orderId={orderSummaryDialog.orderId}
        onClose={() => setOrderSummaryDialog({ open: false, orderId: null })}
      />

      <AdminEditDialogV2
        open={adminEditDialog.open}
        onClose={() => setAdminEditDialog({ open: false, step: null })}
        step={adminEditDialog.step}
        onSave={handleAdminAdjust}
        onRevised={() => loadData(false)}
      />

      {isStaff && (
        <AdminWOUndoCompletionDialogV2
          open={undoDialog.open}
          workOrderId={undoDialog.step?.work_order_id}
          woNumber={undoDialog.step?.wo_number}
          onClose={() => setUndoDialog({ open: false, step: null })}
          onUndone={(r) => {
            toast.success(`${r.wo_number} put back to planned — ${r.quantity_removed} removed from stock`);
            loadData(false);
          }}
        />
      )}

      <DeleteDialogV2
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, step: null })}
        step={deleteDialog.step}
        onDelete={handleCancelWO}
        isStaff={isStaff}
        onHardDeleted={() => loadData(false)}
      />

      {/* Scrap a part of the unit on one step row (remade on the spot) */}
      <ScrapComponentDialogV2
        open={scrapPartDialog.open}
        step={scrapPartDialog.step}
        onClose={() => setScrapPartDialog({ open: false, step: null })}
        onRecorded={() => loadData(false)}
      />
    </Box>
  );
}
